import { ChangeEvent, CSSProperties, useState } from 'react';
import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, SelectChangeEvent, Typography } from '@mui/material';
import { css } from '@emotion/react';

import { ILazyDropdownOption } from 'components/lazyDropdown/LazyDropdown';
import { DataSourceDropdown } from 'features/layer/components/DataSourceDropdown';
import { GroupLayerTreeView } from 'features/layer/components/GroupLayerTreeView';
import { IGroupLayer } from 'features/layer/models/interfaces/IGroupLayer';
import { SridDropdown } from 'features/spatial/components/SridDropdown';
import { PermissionType } from 'features/seismic/models/enums/PermissionType';
import { IQueueParamProps } from '../../../models/interfaces/IQueueParamProps';
import { InterpolationMethod } from '../models/enums/InterpolationMethod';

const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    gap: 16,
    '& .param-fields-row': {
        display: 'flex',
        gap: 16,
    },
    '& .field-srid': {
        flex: '0 0 220px',
    },
    '& .field-permission': {
        flex: '0 0 150px',
    },
    '& .field-data-source': {
        flex: '0 0 220px',
    },
});

const colorInputStyle: CSSProperties = {
    width: 60,
    height: 36,
    padding: 0,
    border: 'none',
    cursor: 'pointer',
};

const DEFAULT_TRANSPARENT_COLOR = '#ffffff';

type IQueueUploadTiffParamProps = IQueueParamProps;

export function QueueUploadTiffParam({ onParamsChange, initialValues }: IQueueUploadTiffParamProps) {
    const iv = initialValues ?? {};
    const initialSrid = iv.Srid ? { value: Number(iv.Srid), label: iv.Srid } as ILazyDropdownOption<number> : null;

    const [srid, setSrid] = useState<ILazyDropdownOption<number> | null>(initialSrid);
    const [dataSource, setDataSource] = useState<number | null>(iv.DataSourceId ? Number(iv.DataSourceId) : null);
    const [groupLayer, setGroupLayer] = useState<IGroupLayer | null>(iv.GroupId ? { Id: Number(iv.GroupId) } as IGroupLayer : null);
    const [permission, setPermission] = useState<PermissionType>(iv.Permission ? Number(iv.Permission) as PermissionType : PermissionType.Private);
    const [interpolationMethod, setInterpolationMethod] = useState<InterpolationMethod>(
        (iv.InterpolationMethod as InterpolationMethod) || InterpolationMethod.Nearest
    );
    const [isMultiband, setIsMultiband] = useState<boolean>(!!iv.TransparentColor);
    const [transparentColor, setTransparentColor] = useState<string>(iv.TransparentColor || DEFAULT_TRANSPARENT_COLOR);

    function notify(
        nextSrid: ILazyDropdownOption<number> | null,
        nextDataSource: number | null,
        nextGroupLayer: IGroupLayer | null,
        nextPermission: PermissionType,
        nextInterpolationMethod: InterpolationMethod,
        nextIsMultiband: boolean,
        nextTransparentColor: string,
    ) {
        onParamsChange?.({
            Srid: String(nextSrid?.value ?? ''),
            DataSourceId: String(nextDataSource ?? ''),
            GroupId: String(nextGroupLayer?.Id ?? ''),
            Permission: String(nextPermission),
            InterpolationMethod: nextInterpolationMethod,
            ...(nextIsMultiband ? { TransparentColor: nextTransparentColor } : {}),
        });
    }

    function handleSridChange(value: ILazyDropdownOption<number> | null) {
        setSrid(value);
        notify(value, dataSource, groupLayer, permission, interpolationMethod, isMultiband, transparentColor);
    }

    function handleDataSourceChange(value: number | null) {
        setDataSource(value);
        setGroupLayer(null);
        notify(srid, value, null, permission, interpolationMethod, isMultiband, transparentColor);
    }

    function handleGroupLayerChange(value: IGroupLayer | null) {
        setGroupLayer(value);
        notify(srid, dataSource, value, permission, interpolationMethod, isMultiband, transparentColor);
    }

    function handlePermissionChange(event: SelectChangeEvent<PermissionType>) {
        const value = event.target.value as PermissionType;
        setPermission(value);
        notify(srid, dataSource, groupLayer, value, interpolationMethod, isMultiband, transparentColor);
    }

    function handleInterpolationMethodChange(event: SelectChangeEvent<InterpolationMethod>) {
        const value = event.target.value as InterpolationMethod;
        setInterpolationMethod(value);
        notify(srid, dataSource, groupLayer, permission, value, isMultiband, transparentColor);
    }

    function handleIsMultibandChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.checked;
        setIsMultiband(value);
        notify(srid, dataSource, groupLayer, permission, interpolationMethod, value, transparentColor);
    }

    function handleTransparentColorChange(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value;
        setTransparentColor(value);
        notify(srid, dataSource, groupLayer, permission, interpolationMethod, isMultiband, value);
    }

    return (
        <Box css={containerStyle}>
            <div className='param-fields-row'>
                <Box className='field-srid'>
                    <SridDropdown value={srid} onChange={handleSridChange} />
                </Box>
                <FormControl className='field-permission'>
                    <InputLabel>Permission</InputLabel>
                    <Select<PermissionType>
                        value={permission}
                        label='Permission'
                        onChange={handlePermissionChange}
                        MenuProps={{ style: { zIndex: 1000000 } }}
                    >
                        <MenuItem value={PermissionType.Private}>Private</MenuItem>
                        <MenuItem value={PermissionType.Public}>Public</MenuItem>
                    </Select>
                </FormControl>
                <Box className='field-data-source'>
                    <DataSourceDropdown value={dataSource} onChange={handleDataSourceChange} />
                </Box>
            </div>
            <GroupLayerTreeView dataSourceId={dataSource} value={groupLayer} onChange={handleGroupLayerChange} />
            <FormControl sx={{ width: 220 }}>
                <InputLabel>Interpolation Method</InputLabel>
                <Select<InterpolationMethod>
                    value={interpolationMethod}
                    label='Interpolation Method'
                    onChange={handleInterpolationMethodChange}
                    MenuProps={{ style: { zIndex: 1000000 } }}
                >
                    <MenuItem value={InterpolationMethod.Nearest}>Nearest</MenuItem>
                    <MenuItem value={InterpolationMethod.Bilinear}>Bilinear</MenuItem>
                    <MenuItem value={InterpolationMethod.Cubic}>Cubic</MenuItem>
                    <MenuItem value={InterpolationMethod.Lanczos}>Lanczos</MenuItem>
                    <MenuItem value={InterpolationMethod.Average}>Average</MenuItem>
                    <MenuItem value={InterpolationMethod.Rms}>Rms</MenuItem>
                    <MenuItem value={InterpolationMethod.Mode}>Mode</MenuItem>
                </Select>
            </FormControl>
            <FormControlLabel
                control={<Checkbox checked={isMultiband} onChange={handleIsMultibandChange} />}
                label='Multiband TIFF'
            />
            {isMultiband && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <Typography variant='body2'>Transparent Color</Typography>
                    <input type='color' value={transparentColor} onChange={handleTransparentColorChange} style={colorInputStyle} />
                </Box>
            )}
        </Box>
    );
}
